import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { api, fullName, formatDate } from '../lib/api';
import { PageHeader, EmptyState } from '../components/Common';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { FileSignature, Plus, FileText, Trash2, Search, Edit2, Eye, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { normalizaTexto } from '../lib/utils';
import PdfPreviewDialog from '../components/PdfPreviewDialog';
import { PatientCombobox } from '../components/PatientCombobox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// EL MÉDICO DEL INFORME (26/10/2026): se elige de las fichas del Catálogo QX
// (viene la de quien lo crea si es médico). Decide el membrete y si va la
// firma del titular (solo si es el titular). «Otro…» deja escribir el nombre
// a mano, y entonces nunca lleva la firma del titular.
const OTRO_MEDICO = '__otro__';

/**
 * Diálogo de creación/edición de informe.
 *
 * El paciente puede ser de la BBDD (via PatientCombobox) o manual (para
 * pacientes ajenos que no están en la ficha). El cuerpo admite markdown ligero.
 *
 * Se exporta porque la ficha del paciente edita con él sus informes
 * (administración, 24/9/2026): un solo editor, no dos que se separen.
 */
export function InformeDialog({ open, onOpenChange, initial, patients, onSaved }) {
  const isEdit = !!initial?.id;
  const empty = {
    patient_id: '',
    patient_name_snapshot: '',
    patient_nif: '',
    title: '',
    body: '',
    doc_date: new Date().toISOString().slice(0, 10),
    doctor_name: '',
  };
  const [form, setForm] = useState(empty);
  const [freeform, setFreeform] = useState(false);
  const [importando, setImportando] = useState(false);
  // El selector «Médico»: lo que dice el servidor (null = el campo de texto
  // de siempre) y el elegido (OTRO_MEDICO = escrito a mano).
  const [medicos, setMedicos] = useState(null);
  const [medicoSel, setMedicoSel] = useState(OTRO_MEDICO);
  // Con qué médico se abrió: al editar, el médico solo se manda si se cambia
  // (abrir y guardar uno de antes no le cambia la firma). `comoAntes`: uno de
  // antes del selector que no casa con ninguna ficha y sale, como siempre,
  // con la firma del titular.
  const [medicoInicial, setMedicoInicial] = useState({ sel: OTRO_MEDICO, nombre: '', comoAntes: false });
  const informeId = initial?.id || '';
  useEffect(() => {
    setMedicos(null);
    setMedicoSel(OTRO_MEDICO);
    setMedicoInicial({ sel: OTRO_MEDICO, nombre: '', comoAntes: false });
    if (!open) return undefined;
    let vivo = true;
    api.get('/api/informes/medicos', { params: informeId ? { informe_id: informeId } : {} }).then((r) => {
      const d = r?.data;
      if (!vivo || !d || !Array.isArray(d.medicos)) return;
      setMedicos(d.medicos);
      setMedicoSel(d.propuesto || OTRO_MEDICO);
      setMedicoInicial({ sel: d.propuesto || OTRO_MEDICO, nombre: d.otro || '', comoAntes: !!d.como_antes });
      if (!d.propuesto && d.otro) setForm((f) => ({ ...f, doctor_name: d.otro }));
    }).catch(() => {});
    return () => { vivo = false; };
  }, [open, informeId]);
  const medicoCambiado = medicoSel !== medicoInicial.sel
    || (medicoSel === OTRO_MEDICO && (form.doctor_name || '').trim() !== medicoInicial.nombre.trim());

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({ ...empty, ...initial });
      setFreeform(!initial.patient_id);
    } else {
      setForm(empty);
      setFreeform(false);
    }
    // eslint-disable-next-line
  }, [open, initial]);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // `elegido` viene del buscador: los pacientes antiguos no están en la lista
  // precargada y el informe se guardaba sin nombre ni NIF.
  const onPickPatient = (pid, elegido) => {
    const p = elegido || patients.find(x => x.id === pid);
    if (!p) return setF('patient_id', pid);
    setForm(f => ({
      ...f,
      patient_id: pid,
      patient_name_snapshot: fullName(p),
      patient_nif: p.nif || '',
    }));
  };

  // Vuelca datos clínicos + todos los evolutivos en el cuerpo. Se añade al
  // final: si ya habías escrito algo, no se pierde.
  const importarHistoria = async () => {
    if (!form.patient_id) return toast.info('Elige antes el paciente');
    setImportando(true);
    try {
      const r = await api.get('/api/informes/historia-paciente', {
        params: { patient_id: form.patient_id },
      });
      const previo = (form.body || '').trim();
      setF('body', previo ? `${previo}\n\n${r.data.body}` : r.data.body);
      toast.success(r.data.episodios
        ? `Historia traída: ${r.data.episodios} evolutivo${r.data.episodios === 1 ? '' : 's'}. Edítala antes de generar.`
        : 'Datos clínicos traídos (este paciente no tiene evolutivos todavía)');
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || 'No se pudo traer la historia');
    } finally {
      setImportando(false);
    }
    return undefined;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error('Escribe el título del informe');
    if (!form.body.trim()) return toast.error('El cuerpo del informe está vacío');
    if (freeform && !form.patient_name_snapshot.trim()) {
      return toast.error('Escribe el nombre del paciente');
    }
    if (!freeform && !form.patient_id) {
      return toast.error('Selecciona un paciente o marca "sin ficha"');
    }
    try {
      const payload = { ...form };
      if (freeform) {
        payload.patient_id = null;
      }
      if (medicos && (!isEdit || medicoCambiado)) {
        // La ficha elegida (su nombre lo pone el servidor) o «Otro…».
        const elegido = medicos.find((m) => m.id === medicoSel);
        payload.medico_id = elegido ? elegido.id : null;
        if (elegido) payload.doctor_name = elegido.name.replace(/ \(de baja\)$/, '');
      }
      if (isEdit) {
        await api.put(`/api/informes/${initial.id}`, payload);
        toast.success('Informe actualizado');
      } else {
        await api.post('/api/informes', payload);
        toast.success('Informe creado');
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al guardar');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="informe-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar informe' : 'Nuevo informe médico'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="flex items-center gap-3 rounded-md border border-[#D9E3DD] bg-[rgba(46,110,94,0.06)] p-3">
            <Switch
              checked={freeform}
              onCheckedChange={(v) => {
                setFreeform(!!v);
                if (v) setF('patient_id', '');
              }}
              data-testid="informe-freeform-switch"
            />
            <div className="text-sm">
              <div className="font-medium text-[#13231E]">Paciente sin ficha</div>
              <div className="text-xs text-muted-foreground">Actívalo para escribir el nombre y DNI a mano (paciente externo)</div>
            </div>
          </div>

          {!freeform ? (
            <div>
              <Label>Paciente</Label>
              <PatientCombobox
                patients={patients}
                value={form.patient_id}
                onChange={onPickPatient}
                placeholder="Buscar por nombre, DNI o teléfono…"
                data-testid="informe-patient-select"
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Nombre del paciente</Label>
                <Input
                  value={form.patient_name_snapshot}
                  onChange={(e) => setF('patient_name_snapshot', e.target.value)}
                  placeholder="Ej.: Ana García López"
                  data-testid="informe-patient-name-manual"
                />
              </div>
              <div>
                <Label>DNI/NIE (opcional)</Label>
                <Input
                  value={form.patient_nif}
                  onChange={(e) => setF('patient_nif', e.target.value)}
                  placeholder="Ej.: 12345678A"
                  data-testid="informe-patient-nif-manual"
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Título del informe</Label>
              <Input
                value={form.title}
                onChange={(e) => setF('title', e.target.value)}
                placeholder="Ej.: Informe médico postoperatorio"
                data-testid="informe-title"
              />
            </div>
            <div>
              <Label>Fecha</Label>
              <Input
                type="date"
                value={form.doc_date}
                onChange={(e) => setF('doc_date', e.target.value)}
                data-testid="informe-date"
              />
            </div>
          </div>

          <div data-testid="informe-medico">
            <Label>{medicos ? 'Médico' : 'Doctor (opcional)'}</Label>
            {medicos && (
              <Select value={medicoSel} onValueChange={setMedicoSel}>
                <SelectTrigger data-testid="informe-medico-select"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-80">
                  {medicos.map((m) => (
                    <SelectItem key={m.id} value={m.id}>{m.name}{m.titular ? ' (titular)' : ''}</SelectItem>
                  ))}
                  <SelectItem value={OTRO_MEDICO}>Otro…</SelectItem>
                </SelectContent>
              </Select>
            )}
            {(!medicos || medicoSel === OTRO_MEDICO) && (
              <Input
                className={medicos ? 'mt-2' : ''}
                value={form.doctor_name}
                onChange={(e) => setF('doctor_name', e.target.value)}
                placeholder={medicos ? 'Nombre del médico, escrito a mano' : 'Nombre del doctor que firma'}
                data-testid="informe-doctor-name"
              />
            )}
            {medicos && medicoSel === OTRO_MEDICO && (
              <p className="mt-1 text-xs text-muted-foreground" data-testid="informe-medico-otro-aviso">
                {medicoInicial.comoAntes && !medicoCambiado
                  ? 'Informe de antes del selector: mientras no cambies el médico, sale como se hizo (con la firma del titular).'
                  : 'Escrito a mano: el informe sale sin la firma del titular.'}
              </p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <Label>Cuerpo del informe</Label>
              {/* Traer la historia entera y podarla es más rápido que teclearla
                  (2026-07-26). Se añade al final, nunca pisa lo ya escrito. */}
              {!freeform && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={importarHistoria}
                  disabled={!form.patient_id || importando}
                  title={form.patient_id
                    ? 'Trae datos clínicos y todos los evolutivos; luego lo editas'
                    : 'Elige antes el paciente'}
                  data-testid="informe-importar-historia"
                >
                  {importando
                    ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Trayendo…</>
                    : <><Download className="w-3.5 h-3.5 mr-1.5 text-[#2E6E5E]" /> Importar historia</>}
                </Button>
              )}
            </div>
            <Textarea
              value={form.body}
              onChange={(e) => setF('body', e.target.value)}
              rows={14}
              placeholder="Pega o escribe el texto del informe.&#10;&#10;Formato disponible:&#10;  ## Subtítulo&#10;  **negrita**&#10;  *cursiva*&#10;  - lista con guiones"
              className="font-mono text-sm"
              data-testid="informe-body"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Se admiten <code>##</code> para subtítulos, <code>**negrita**</code>, <code>*cursiva*</code> y listas con <code>- </code>.
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="informe-save">
              {isEdit ? 'Guardar cambios' : 'Crear informe'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


export default function InformesPage() {
  const [items, setItems] = useState([]);
  const [patients, setPatients] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const [informes, pats] = await Promise.all([
        api.get('/api/informes'),
        api.get('/api/patients'),
      ]);
      setItems(informes.data);
      setPatients(pats.data);
    } catch (e) {
      toast.error('No se pudo cargar la lista de informes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  // Deep-link ?edit=<id>: abre el editor directamente (p. ej. al generar la
  // "historia completa" desde la ficha del paciente). Solo la primera vez.
  const [searchParams, setSearchParams] = useSearchParams();
  const editParamDone = useRef(false);
  useEffect(() => {
    if (editParamDone.current || loading) return;
    const editId = searchParams.get('edit');
    if (!editId) return;
    const item = items.find((x) => x.id === editId);
    editParamDone.current = true;
    if (item) {
      setEditing(item);
      setDialogOpen(true);
    }
    setSearchParams({}, { replace: true });
  }, [loading, items, searchParams, setSearchParams]);

  const filtered = useMemo(() => {
    const s = normalizaTexto(q.trim());
    if (!s) return items;
    return items.filter(x =>
      normalizaTexto(x.patient_name_snapshot).includes(s) ||
      normalizaTexto(x.patient_nif).includes(s) ||
      normalizaTexto(x.title).includes(s)
    );
  }, [items, q]);

  // Borrado BLANDO desde el 24/9/2026: desaparece de todas partes, pero queda
  // registrado quién lo borra y se puede recuperar. Ya no es «para siempre».
  const onDelete = async (it) => {
    const aviso = `¿Borrar el informe «${it.title || 'sin título'}» de ${it.patient_name_snapshot || 'este paciente'}`
      + `${it.doc_date ? ` (${formatDate(it.doc_date)})` : ''}?\n\n`
      + 'Desaparece de la ficha y de las listas. Queda registrado quién lo borra y se puede recuperar.';
    if (!window.confirm(aviso)) return;
    try {
      await api.delete(`/api/informes/${it.id}`);
      toast.success('Informe borrado');
      fetchItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se ha podido borrar el informe');
    }
  };

  const openPreview = (id, title) => {
    setPdfPreview({
      url: `${BACKEND_URL}/api/informes/${id}/pdf`,
      filename: `informe_${id}.pdf`,
      title: `Informe · ${title || ''}`,
    });
  };

  const openNew = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (item) => { setEditing(item); setDialogOpen(true); };

  return (
    <div>
      <PageHeader
        title="Informes médicos"
        subtitle={`${items.length} informes emitidos`}
        actions={
          <Button onClick={openNew} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="informe-new">
            <Plus className="w-4 h-4 mr-2" /> Nuevo informe
          </Button>
        }
      />

      <div className="cjo-card p-4 mb-5">
        <div className="relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por paciente, DNI o título…"
            className="pl-9"
            data-testid="informes-search"
          />
        </div>
      </div>

      {loading ? (
        <div className="cjo-card p-8 text-center text-sm text-muted-foreground">Cargando…</div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={FileSignature}
          title={q ? 'Sin resultados' : 'Aún no hay informes'}
          description={q ? 'Prueba con otro término.' : 'Pulsa "Nuevo informe" para redactar tu primer informe médico.'}
        />
      ) : (
        <div className="cjo-card overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#F0F6F2] border-b border-[#D9E3DD]">
              <tr className="text-left text-xs uppercase tracking-wider text-[#55677a]">
                <th className="py-3 px-4">Fecha</th>
                <th className="py-3 px-4">Paciente</th>
                <th className="py-3 px-4">DNI</th>
                <th className="py-3 px-4">Título</th>
                <th className="py-3 px-4">Autor</th>
                <th className="py-3 px-4 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#D9E3DD]">
              {filtered.map((it) => (
                <tr key={it.id} className="hover:bg-[rgba(46,110,94,0.05)]" data-testid={`informe-row-${it.id}`}>
                  <td className="py-3 px-4 text-sm tabular-nums">{formatDate(it.doc_date)}</td>
                  <td className="py-3 px-4 text-sm">
                    <div className="font-medium">{it.patient_name_snapshot}</div>
                    {it.is_freeform ? <Badge variant="outline" className="text-[10px] mt-0.5">Sin ficha</Badge> : null}
                  </td>
                  <td className="py-3 px-4 text-sm text-muted-foreground">{it.patient_nif || '—'}</td>
                  <td className="py-3 px-4 text-sm">{it.title}</td>
                  <td className="py-3 px-4 text-xs text-muted-foreground">{it.created_by_name || ''}</td>
                  <td className="py-3 px-4 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => openPreview(it.id, it.title)} data-testid={`informe-preview-${it.id}`}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(it)} data-testid={`informe-edit-${it.id}`}>
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => onDelete(it)} className="text-destructive" data-testid={`informe-delete-${it.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <InformeDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initial={editing}
        patients={patients}
        onSaved={fetchItems}
      />

      {pdfPreview && (
        <PdfPreviewDialog
          open={!!pdfPreview}
          onOpenChange={(v) => !v && setPdfPreview(null)}
          url={pdfPreview.url}
          filename={pdfPreview.filename}
          title={pdfPreview.title}
        />
      )}
    </div>
  );
}
